import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { StockApi } from "../../api/StockApi";
import type { CatalogItemInstanceFetchOptions } from "../../generated/dto/CatalogItemInstanceFetchOptions";
import type { CatalogItemInstanceStaff } from "../../generated/dto/CatalogItemInstanceStaff";
import type { CreateStockChange } from "../../generated/dto/CreateStockChange";
import type { DeleteStockChangeStaff } from "../../generated/dto/DeleteStockChangeStaff";
import type { StaffStockSummary } from "../../generated/dto/StaffStockSummary";
import type { StockAdjustRequest } from "../../generated/dto/StockAdjustRequest";
import type { StockChangeFetchOptions } from "../../generated/dto/StockChangeFetchOptions";
import type { StockChangeStaff } from "../../generated/dto/StockChangeStaff";
import type { UpdateStockChange } from "../../generated/dto/UpdateStockChange";

export class StockApiClient implements StockApi {
  constructor(private readonly client = useClient()) {}

  async getStockChangeStaff(id: number, options?: StockChangeFetchOptions): Promise<StockChangeStaff> {
    const { data } = await this.client.get<unknown>(`/stockchange/staff/${id}`, { params: serializeCbiDto(options, "generated.dto.StockChangeFetchOptions") });
    return deserializeCbiDto<StockChangeStaff>(data, "generated.dto.StockChangeStaff");
  }

  async createStockChangeStaff(source: CreateStockChange): Promise<StockChangeStaff> {
    const { data } = await this.client.post<unknown>(`/stockchange/staff`, serializeCbiDto(source, "generated.dto.CreateStockChange"));
    return deserializeCbiDto<StockChangeStaff>(data, "generated.dto.StockChangeStaff");
  }

  async updateStockChangeStaff(source: UpdateStockChange): Promise<StockChangeStaff> {
    const { data } = await this.client.put<unknown>(`/stockchange/staff/${source.assignId}`, serializeCbiDto(source, "generated.dto.UpdateStockChange"));
    return deserializeCbiDto<StockChangeStaff>(data, "generated.dto.StockChangeStaff");
  }

  async deleteStockChangeStaff(source: DeleteStockChangeStaff): Promise<void> {
    await this.client.delete(`/stockchange/staff/${source.assignId}`);
  }

  async getStockChangeStaffItemInstanceId(id: number, options?: CatalogItemInstanceFetchOptions): Promise<CatalogItemInstanceStaff> {
    const { data } = await this.client.get<unknown>(`/stockchange/staff/${id}/iteminstanceid`, { params: serializeCbiDto(options, "generated.dto.CatalogItemInstanceFetchOptions") });
    return deserializeCbiDto<CatalogItemInstanceStaff>(data, "generated.dto.CatalogItemInstanceStaff");
  }

  async adjustStockStaff(request: StockAdjustRequest): Promise<StaffStockSummary> {
    const { data } = await this.client.post<unknown>(`/stock/staff/adjuststock`, serializeCbiDto(request, "generated.dto.StockAdjustRequest"));
    return deserializeCbiDto<StaffStockSummary>(data, "generated.dto.StaffStockSummary");
  }

  async stockSummaryStaff(): Promise<Array<StaffStockSummary>> {
    const { data } = await this.client.post<unknown>(`/stock/staff/stocksummary`);
    return deserializeCbiDto<Array<StaffStockSummary>>(data, "generated.dto.StaffStockSummary");
  }
}

export const useStockApi = (): StockApi => new StockApiClient();
